/**
 * Resize an image File to a small square-ish JPEG data-URI suitable for an
 * avatar, reducing quality until it fits under the server's size cap. Runs
 * entirely in the browser (canvas) — no upload of the original file.
 */
export async function fileToAvatarDataUri(file: File, maxDim = 256): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Your browser can't process images here.");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  // Keep well under the 60KB server cap (data-URI length, not raw bytes).
  for (const quality of [0.75, 0.6, 0.45, 0.3]) {
    const uri = canvas.toDataURL("image/jpeg", quality);
    if (uri.length <= 58000) return uri;
  }
  throw new Error("That image is too detailed — please try a simpler photo.");
}

/**
 * Resize an image File to a wide logo data-URI.
 *
 * Deliberately different from fileToAvatarDataUri in two ways: it keeps a wider
 * max dimension (logos are wordmarks, not square crops), and it tries PNG FIRST
 * so transparency survives — a JPEG logo renders as a white box on a tinted
 * band. PNG has no quality dial, so if the PNG is too big we fall back to JPEG
 * and step quality down, accepting the white background over a failed upload.
 */
export async function fileToLogoDataUri(file: File, maxDim = 420): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Your browser can't process images here.");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  // Under the 58000 the schema allows, which is itself under the 64KB body cap.
  const png = canvas.toDataURL("image/png");
  if (png.length <= 57000) return png;

  for (const quality of [0.85, 0.7, 0.55, 0.4]) {
    const uri = canvas.toDataURL("image/jpeg", quality);
    if (uri.length <= 57000) return uri;
  }
  throw new Error("That logo is too detailed — try a simpler or smaller image.");
}
